import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ArrowLeft, Save, Plus, GripVertical, Trash2, ChevronDown, ChevronUp, Eye, Settings2, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import SectionRenderer, { SECTION_TYPES } from "@/components/site/SectionRenderer";
import RichTextEditor from "@/components/admin/RichTextEditor";
import FileUpload from "@/components/admin/FileUpload";
import GalleryPicker from "@/components/admin/GalleryPicker";
import { bi, biSet, emptyBi } from "@/lib/bilingual";

const genId = () => Math.random().toString(36).slice(2, 9);

const PageEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [editLang, setEditLang] = useState("id");

  useEffect(() => {
    api.get(`/pages/${id}`).then(({ data }) => setPage(data)).catch(() => nav("/admin/pages"));
  }, [id, nav]);

  const setField = (k, v) => setPage({ ...page, [k]: v });

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const sections = Array.from(page.sections || []);
    const [moved] = sections.splice(result.source.index, 1);
    sections.splice(result.destination.index, 0, moved);
    setPage({ ...page, sections });
  };

  const addSection = (type) => {
    const def = SECTION_TYPES.find((t) => t.type === type);
    if (!def) return;
    const sec = { id: genId(), type, props: JSON.parse(JSON.stringify(def.defaults)) };
    setPage({ ...page, sections: [...(page.sections || []), sec] });
    setAddOpen(false);
    setExpandedIds(new Set([...expandedIds, sec.id]));
  };

  const removeSection = (secId) => {
    if (!window.confirm("Hapus section ini?")) return;
    setPage({ ...page, sections: page.sections.filter((s) => s.id !== secId) });
  };

  const updateSection = (secId, props) => {
    setPage({
      ...page,
      sections: page.sections.map((s) => (s.id === secId ? { ...s, props: { ...s.props, ...props } } : s)),
    });
  };

  const toggleExpand = (secId) => {
    const next = new Set(expandedIds);
    if (next.has(secId)) next.delete(secId);
    else next.add(secId);
    setExpandedIds(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: page.title,
        slug: page.slug,
        seo_title: page.seo_title || emptyBi(),
        seo_description: page.seo_description || emptyBi(),
        sections: page.sections,
        published: page.published,
      };
      const { data } = await api.put(`/pages/${id}`, payload);
      setPage(data);
      toast.success("Halaman disimpan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!page) return <div className="text-zinc-500">Loading...</div>;

  const publicPath = page.public_path || (page.route_key ? null : `/p/${page.slug}`);

  if (previewing) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-3">
          <span className="text-sm text-amber-900 font-semibold">Mode Preview</span>
          <button onClick={() => setPreviewing(false)} className="text-sm font-semibold text-amber-900 hover:underline">← Kembali ke Editor</button>
        </div>
        <div className="rounded-2xl border border-zinc-200 overflow-hidden bg-white">
          {(page.sections || []).map((s) => <SectionRenderer key={s.id} section={s} />)}
          {(page.sections || []).length === 0 && <div className="text-center text-zinc-400 py-20">Belum ada section</div>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/pages" className="p-2 rounded-lg hover:bg-zinc-100"><ArrowLeft size={16} /></Link>
          <div>
            <h1 className="text-2xl font-display font-bold">{page.title}</h1>
            <p className="text-xs text-zinc-500 font-mono">
              {page.is_system ? `Sistem · ${page.route_key} · ${publicPath}` : `/p/${page.slug}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-zinc-200 overflow-hidden text-xs font-bold">
            <button type="button" onClick={() => setEditLang("id")} className={`px-3 py-1.5 ${editLang === "id" ? "bg-maroon-800 text-white" : "bg-white text-zinc-600"}`}>ID</button>
            <button type="button" onClick={() => setEditLang("en")} className={`px-3 py-1.5 ${editLang === "en" ? "bg-maroon-800 text-white" : "bg-white text-zinc-600"}`}>EN</button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!page.published} onChange={(e) => setField("published", e.target.checked)} data-testid="page-published" />
            Publish
          </label>
          {publicPath && (
            <a href={publicPath} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-zinc-300 text-sm font-semibold hover:bg-zinc-100">
              <ExternalLink size={14} /> Lihat
            </a>
          )}
          <button onClick={() => setPreviewing(true)} data-testid="page-preview" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-300 text-zinc-800 font-semibold hover:bg-zinc-100">
            <Eye size={14} /> Preview
          </button>
          <button onClick={save} disabled={saving} data-testid="save-page" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900 disabled:opacity-60">
            <Save size={14} /> {saving ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="sections">
              {(prov) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-3" data-testid="sections-list">
                  {(page.sections || []).map((s, idx) => (
                    <Draggable key={s.id} draggableId={s.id} index={idx}>
                      {(p2) => (
                        <div ref={p2.innerRef} {...p2.draggableProps} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                          <div className="flex items-center gap-3 p-4 bg-zinc-50 border-b border-zinc-200">
                            <span {...p2.dragHandleProps} className="cursor-grab text-zinc-400 hover:text-zinc-700"><GripVertical size={16} /></span>
                            <span className="text-xs font-bold uppercase tracking-widest text-maroon-700">{s.type.replace(/_/g, " ")}</span>
                            <span className="text-sm text-zinc-700 truncate flex-1">
                              {bi(s.props?.title, editLang) || bi(s.props?.overline, editLang) || `Section ${idx + 1}`}
                            </span>
                            <button onClick={() => toggleExpand(s.id)} className="p-1.5 rounded hover:bg-white">
                              {expandedIds.has(s.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <button onClick={() => removeSection(s.id)} data-testid={`remove-section-${idx}`} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                          </div>
                          {expandedIds.has(s.id) && (
                            <div className="p-5">
                              <SectionForm section={s} lang={editLang} onChange={(props) => updateSection(s.id, props)} />
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {prov.placeholder}
                  {(page.sections || []).length === 0 && (
                    <div className="text-center py-16 border-2 border-dashed border-zinc-200 rounded-2xl text-zinc-500 text-sm">
                      Belum ada section. Klik "Tambah Section" di panel kanan.
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>

        <div className="space-y-4">
          <div className="bg-white p-5 rounded-2xl border border-zinc-200">
            <button onClick={() => setAddOpen((v) => !v)} data-testid="add-section-toggle"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900">
              <Plus size={16} /> Tambah Section
            </button>
            {addOpen && (
              <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
                {SECTION_TYPES.map((t) => (
                  <button key={t.type} onClick={() => addSection(t.type)} data-testid={`add-section-${t.type}`}
                    className="w-full text-left px-4 py-2.5 rounded-lg bg-zinc-50 hover:bg-maroon-50 hover:text-maroon-800 text-sm font-medium transition">
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white p-5 rounded-2xl border border-zinc-200">
            <h3 className="font-display font-bold text-sm text-zinc-900 mb-3 flex items-center gap-2"><Settings2 size={14} /> Meta Halaman</h3>
            <div className="space-y-3">
              <F label="Judul (admin)" value={page.title} onChange={(v) => setField("title", v)} testid="meta-title" />
              {!page.is_system && (
                <F label="Slug (URL)" value={page.slug} onChange={(v) => setField("slug", v)} testid="meta-slug" />
              )}
              {page.is_system && (
                <p className="text-xs text-zinc-500">Route: <code>{page.route_key}</code> → <code>{publicPath}</code></p>
              )}
              <BiF label="SEO Title" value={page.seo_title} lang={editLang} onChange={(v) => setField("seo_title", v)} />
              <BiArea label="SEO Description" value={page.seo_description} lang={editLang} onChange={(v) => setField("seo_description", v)} rows={3} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const F = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-700 mb-1">{label}</label>
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm outline-none focus:border-maroon-700" />
  </div>
);

const BiF = ({ label, value, lang, onChange }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-700 mb-1">{label} <span className="text-maroon-700 uppercase">({lang})</span></label>
    <input value={bi(value, lang)} onChange={(e) => onChange(biSet(value, lang, e.target.value))}
      className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm outline-none focus:border-maroon-700" />
  </div>
);

const BiArea = ({ label, value, lang, onChange, rows = 3 }) => (
  <div>
    <label className="block text-xs font-semibold text-zinc-700 mb-1">{label} <span className="text-maroon-700 uppercase">({lang})</span></label>
    <textarea rows={rows} value={bi(value, lang)} onChange={(e) => onChange(biSet(value, lang, e.target.value))}
      className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm outline-none focus:border-maroon-700" />
  </div>
);

const SectionForm = ({ section, onChange, lang }) => {
  const p = section.props || {};
  const setBi = (key, v) => onChange({ [key]: biSet(p[key], lang, v) });

  switch (section.type) {
    case "home_hero":
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <BiF label="Badge" value={p.badge} lang={lang} onChange={(v) => onChange({ badge: v })} />
          <BiF label="CTA Primary" value={p.ctaPrimary} lang={lang} onChange={(v) => onChange({ ctaPrimary: v })} />
          <BiF label="Title sebelum highlight" value={p.titleBefore} lang={lang} onChange={(v) => onChange({ titleBefore: v })} />
          <BiF label="Title highlight" value={p.titleHighlight} lang={lang} onChange={(v) => onChange({ titleHighlight: v })} />
          <BiF label="Title setelah highlight" value={p.titleAfter} lang={lang} onChange={(v) => onChange({ titleAfter: v })} />
          <BiF label="CTA Secondary" value={p.ctaSecondary} lang={lang} onChange={(v) => onChange({ ctaSecondary: v })} />
          <div className="sm:col-span-2"><BiArea label="Lead" value={p.lead} lang={lang} onChange={(v) => onChange({ lead: v })} /></div>
          <F label="CTA Secondary Link" value={p.ctaSecondaryLink} onChange={(v) => onChange({ ctaSecondaryLink: v })} />
          <label className="flex items-center gap-2 text-sm mt-6">
            <input type="checkbox" checked={p.showStats !== false} onChange={(e) => onChange({ showStats: e.target.checked })} /> Tampilkan stats
          </label>
          <div className="sm:col-span-2">
            <FileUpload label="Background Image" value={p.bgImage} onChange={(v) => onChange({ bgImage: v })} folder="pages" aspect="wide" />
          </div>
          <div className="sm:col-span-2">
            <FileUpload
              label="Gambar kanan (produk — animasi naik-turun)"
              value={p.sideImage || ""}
              onChange={(v) => onChange({ sideImage: v })}
              folder="pages"
              aspect="square"
              fit="contain"
              testid="home-hero-side-image"
            />
            <p className="mt-1.5 text-xs text-zinc-500">Kosongkan untuk memakai gambar default produk.</p>
          </div>
          <BiF label="Kartu floating (Halal)" value={p.floatHalal} lang={lang} onChange={(v) => onChange({ floatHalal: v })} />
          <BiF label="Kartu floating (Rating)" value={p.floatRating} lang={lang} onChange={(v) => onChange({ floatRating: v })} />
        </div>
      );
    case "page_hero":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <BiArea label="Lead" value={p.lead} lang={lang} onChange={(v) => onChange({ lead: v })} />
          <FileUpload label="Background (opsional)" value={p.bgImage || ""} onChange={(v) => onChange({ bgImage: v })} folder="pages" aspect="wide" />
        </div>
      );
    case "hero":
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="CTA Text" value={p.ctaText} lang={lang} onChange={(v) => onChange({ ctaText: v })} />
          <div className="sm:col-span-2"><BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} /></div>
          <div className="sm:col-span-2"><BiArea label="Subjudul" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} /></div>
          <F label="CTA Link ('#wa' = WhatsApp)" value={p.ctaLink} onChange={(v) => onChange({ ctaLink: v })} />
          <F label="Tinggi Hero (px)" value={p.height || 480} onChange={(v) => onChange({ height: parseInt(v, 10) || 480 })} />
          <div className="sm:col-span-2">
            <FileUpload label="Background Image" value={p.bgImage} onChange={(v) => onChange({ bgImage: v })} folder="pages" aspect="wide" />
          </div>
        </div>
      );
    case "image_text":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <BiArea label="Body" value={p.body} lang={lang} onChange={(v) => onChange({ body: v })} rows={6} />
          <F
            label="Link Video YouTube (opsional)"
            value={p.videoUrl || ""}
            onChange={(v) => onChange({ videoUrl: v })}
          />
          <p className="text-xs text-zinc-500 -mt-1">
            Contoh: https://youtu.be/xxxxx. Jika diisi, gambar di bawah dipakai sebagai thumbnail (klik play untuk putar).
          </p>
          <BiF label="Alt gambar / judul video" value={p.imageAlt} lang={lang} onChange={(v) => onChange({ imageAlt: v })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!p.flip} onChange={(e) => onChange({ flip: e.target.checked })} /> Flip (media kanan)
          </label>
          <FileUpload label="Gambar / Thumbnail video" value={p.image} onChange={(v) => onChange({ image: v })} folder="pages" aspect="wide" fit="cover" />
        </div>
      );
    case "rich_text":
      return (
        <div>
          <label className="block text-xs font-semibold text-zinc-700 mb-1">Konten <span className="text-maroon-700 uppercase">({lang})</span></label>
          <RichTextEditor value={bi(p.content, lang)} onChange={(v) => setBi("content", v)} />
        </div>
      );
    case "image":
      return (
        <div className="space-y-3">
          <FileUpload label="Gambar" value={p.url} onChange={(v) => onChange({ url: v })} folder="pages" aspect="wide" />
          <BiF label="Alt Text" value={p.alt} lang={lang} onChange={(v) => onChange({ alt: v })} />
          <BiF label="Caption" value={p.caption} lang={lang} onChange={(v) => onChange({ caption: v })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!p.fullwidth} onChange={(e) => onChange({ fullwidth: e.target.checked })} /> Full-width
          </label>
        </div>
      );
    case "cta":
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} /></div>
          <div className="sm:col-span-2"><BiArea label="Subjudul" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} rows={2} /></div>
          <BiF label="Primary Text" value={p.primaryText} lang={lang} onChange={(v) => onChange({ primaryText: v })} />
          <F label="Primary Link" value={p.primaryLink} onChange={(v) => onChange({ primaryLink: v })} />
          <BiF label="Secondary Text" value={p.secondaryText} lang={lang} onChange={(v) => onChange({ secondaryText: v })} />
          <F label="Secondary Link" value={p.secondaryLink} onChange={(v) => onChange({ secondaryLink: v })} />
        </div>
      );
    case "two_column":
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <BiF label="Kiri: Judul" value={p.leftTitle} lang={lang} onChange={(v) => onChange({ leftTitle: v })} />
          <BiF label="Kanan: Judul" value={p.rightTitle} lang={lang} onChange={(v) => onChange({ rightTitle: v })} />
          <BiArea label="Kiri: Konten" value={p.leftContent} lang={lang} onChange={(v) => onChange({ leftContent: v })} rows={4} />
          <BiArea label="Kanan: Konten" value={p.rightContent} lang={lang} onChange={(v) => onChange({ rightContent: v })} rows={4} />
          <FileUpload label="Kiri: Gambar" value={p.leftImage || ""} onChange={(v) => onChange({ leftImage: v })} folder="pages" aspect="wide" />
          <FileUpload label="Kanan: Gambar" value={p.rightImage || ""} onChange={(v) => onChange({ rightImage: v })} folder="pages" aspect="wide" />
        </div>
      );
    case "cards_grid":
      return (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
            <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
            <BiArea label="Subtitle" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} rows={2} />
            <F label="Columns (2/3/4)" value={p.columns || 3} onChange={(v) => onChange({ columns: parseInt(v, 10) || 3 })} />
            <F label="Icon set (services/advantages/values)" value={p.iconSet || "services"} onChange={(v) => onChange({ iconSet: v })} />
            <BiF label="CTA Text" value={p.ctaText} lang={lang} onChange={(v) => onChange({ ctaText: v })} />
            <F label="CTA Link" value={p.ctaLink || ""} onChange={(v) => onChange({ ctaLink: v })} />
          </div>
          <div className="space-y-2">
            {(p.cards || []).map((c, i) => (
              <div key={i} className="p-3 rounded-lg bg-zinc-50 space-y-2">
                <BiF label={`Kartu ${i + 1}: Judul`} value={c.title} lang={lang} onChange={(v) => onChange({ cards: p.cards.map((x, k) => (k === i ? { ...x, title: v } : x)) })} />
                <BiArea label="Deskripsi" value={c.desc} lang={lang} onChange={(v) => onChange({ cards: p.cards.map((x, k) => (k === i ? { ...x, desc: v } : x)) })} rows={2} />
                <FileUpload label="Gambar kartu (opsional)" value={c.image || ""} onChange={(v) => onChange({ cards: p.cards.map((x, k) => (k === i ? { ...x, image: v } : x)) })} folder="pages" aspect="square" />
                <button type="button" onClick={() => onChange({ cards: p.cards.filter((_, k) => k !== i) })} className="text-xs text-red-600 hover:underline">Hapus kartu</button>
              </div>
            ))}
            <button type="button" onClick={() => onChange({ cards: [...(p.cards || []), { title: emptyBi("Kartu baru", "New card"), desc: emptyBi("", ""), image: "" }] })} className="text-xs text-maroon-800 hover:underline font-semibold">+ Tambah kartu</button>
          </div>
        </div>
      );
    case "process_steps":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <BiArea label="Subtitle" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} rows={2} />
          {(p.steps || []).map((s, i) => (
            <div key={i} className="p-3 rounded-lg bg-zinc-50 space-y-2">
              <BiF label={`Langkah ${i + 1}`} value={s.title} lang={lang} onChange={(v) => onChange({ steps: p.steps.map((x, k) => (k === i ? { ...x, title: v } : x)) })} />
              <BiArea label="Deskripsi" value={s.desc} lang={lang} onChange={(v) => onChange({ steps: p.steps.map((x, k) => (k === i ? { ...x, desc: v } : x)) })} rows={2} />
              <button type="button" onClick={() => onChange({ steps: p.steps.filter((_, k) => k !== i) })} className="text-xs text-red-600">Hapus</button>
            </div>
          ))}
          <button type="button" onClick={() => onChange({ steps: [...(p.steps || []), { title: emptyBi("Langkah", "Step"), desc: emptyBi("", "") }] })} className="text-xs text-maroon-800 font-semibold">+ Tambah langkah</button>
        </div>
      );
    case "certs_marquee":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <p className="text-xs text-zinc-500 rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2">
            Logo sertifikasi diambil dari <strong>Pengaturan → Logo Sertifikasi</strong>. Teks di bawah dipakai sebagai cadangan jika belum ada logo.
          </p>
          <label className="block text-xs font-semibold text-zinc-700 mb-1">Cadangan teks (pisah koma)</label>
          <input
            value={(p.items || []).join(", ")}
            onChange={(e) => onChange({ items: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
            className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm outline-none focus:border-maroon-700"
          />
        </div>
      );
    case "certs_grid":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <BiArea label="Subtitle" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} rows={2} />
          <p className="text-xs text-zinc-500 rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2">
            Logo ditampilkan dari <strong>Pengaturan → Logo Sertifikasi</strong> (grid diam, tanpa slide). Item teks di bawah hanya cadangan jika belum ada logo.
          </p>
          {(p.items || []).map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-zinc-50 grid sm:grid-cols-2 gap-2">
              <F label="Kode" value={item.code} onChange={(v) => onChange({ items: p.items.map((x, k) => (k === i ? { ...x, code: v } : x)) })} />
              <BiF label="Deskripsi" value={item.desc} lang={lang} onChange={(v) => onChange({ items: p.items.map((x, k) => (k === i ? { ...x, desc: v } : x)) })} />
              <button type="button" onClick={() => onChange({ items: p.items.filter((_, k) => k !== i) })} className="text-xs text-red-600 sm:col-span-2 text-left">Hapus</button>
            </div>
          ))}
          <button type="button" onClick={() => onChange({ items: [...(p.items || []), { code: "CERT", desc: emptyBi("", "") }] })} className="text-xs text-maroon-800 font-semibold">+ Tambah</button>
        </div>
      );
    case "services_list":
      return (
        <div className="space-y-3">
          {(p.items || []).map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-zinc-50 space-y-2 border border-zinc-100">
              <div className="text-xs font-bold text-zinc-500">Layanan {i + 1}</div>
              <BiF label="Judul" value={item.title} lang={lang} onChange={(v) => onChange({ items: p.items.map((x, k) => (k === i ? { ...x, title: v } : x)) })} />
              <BiArea label="Deskripsi" value={item.desc} lang={lang} onChange={(v) => onChange({ items: p.items.map((x, k) => (k === i ? { ...x, desc: v } : x)) })} rows={3} />
              <label className="block text-xs font-semibold text-zinc-700 mb-1">Bullets (satu baris = satu bullet, bahasa {lang})</label>
              <textarea
                rows={3}
                value={(item.bullets || []).map((b) => bi(b, lang)).join("\n")}
                onChange={(e) => {
                  const lines = e.target.value.split("\n");
                  const bullets = lines.map((line, j) => biSet((item.bullets || [])[j], lang, line));
                  onChange({ items: p.items.map((x, k) => (k === i ? { ...x, bullets } : x)) });
                }}
                className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm outline-none focus:border-maroon-700"
              />
              <GalleryPicker
                label="Foto dari Galeri"
                value={item.image || ""}
                galleryId={item.galleryId || ""}
                aspect="wide"
                onChange={({ image, galleryId }) => onChange({
                  items: p.items.map((x, k) => (k === i ? { ...x, image, galleryId } : x)),
                })}
              />
              <button type="button" onClick={() => onChange({ items: p.items.filter((_, k) => k !== i) })} className="text-xs text-red-600">Hapus layanan</button>
            </div>
          ))}
          <button type="button" onClick={() => onChange({ items: [...(p.items || []), { title: emptyBi("Layanan", "Service"), desc: emptyBi("", ""), bullets: [], image: "", galleryId: "" }] })} className="text-xs text-maroon-800 font-semibold">+ Tambah layanan</button>
        </div>
      );
    case "products_feed":
    case "articles_feed":
    case "gallery_feed":
      return (
        <div className="grid sm:grid-cols-2 gap-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <div className="sm:col-span-2"><BiArea label="Subtitle" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} rows={2} /></div>
          {section.type === "products_feed" && (
            <>
              <F label="Limit (0 = semua)" value={p.limit ?? 0} onChange={(v) => onChange({ limit: parseInt(v, 10) || 0 })} />
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={p.showFilters !== false} onChange={(e) => onChange({ showFilters: e.target.checked })} /> Tampilkan filter kategori
              </label>
              <BiF label="CTA Text" value={p.ctaText} lang={lang} onChange={(v) => onChange({ ctaText: v })} />
              <F label="CTA Link" value={p.ctaLink || ""} onChange={(v) => onChange({ ctaLink: v })} />
            </>
          )}
          <p className="sm:col-span-2 text-xs text-zinc-500">Data diambil otomatis dari CRUD (Produk / Artikel / Galeri).</p>
        </div>
      );
    case "faq_feed":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <BiArea label="Subtitle" value={p.subtitle} lang={lang} onChange={(v) => onChange({ subtitle: v })} rows={2} />
          <BiF label="Still title" value={p.stillTitle} lang={lang} onChange={(v) => onChange({ stillTitle: v })} />
          <BiArea label="Still body" value={p.stillBody} lang={lang} onChange={(v) => onChange({ stillBody: v })} rows={2} />
          <p className="text-xs text-zinc-500">FAQ diambil dari menu Admin → FAQ.</p>
        </div>
      );
    case "testimonials_feed":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <p className="text-xs text-zinc-500">Testimoni diambil dari menu Admin → Testimoni.</p>
        </div>
      );
    case "clients_marquee":
      return (
        <div className="space-y-3">
          <BiF label="Overline" value={p.overline} lang={lang} onChange={(v) => onChange({ overline: v })} />
          <BiF label="Judul" value={p.title} lang={lang} onChange={(v) => onChange({ title: v })} />
          <p className="text-xs text-zinc-500 rounded-lg bg-zinc-50 border border-zinc-100 px-3 py-2">
            Logo diambil dari <strong>Pengaturan → Logo Klien Maklon</strong>. Baris 1 slide ke kanan, baris 2 ke kiri.
          </p>
        </div>
      );
    case "contact_block":
      return (
        <div className="space-y-3">
          <BiF label="Info title" value={p.infoTitle} lang={lang} onChange={(v) => onChange({ infoTitle: v })} />
          <BiArea label="Info lead" value={p.infoLead} lang={lang} onChange={(v) => onChange({ infoLead: v })} rows={2} />
          <BiF label="Form title" value={p.formTitle} lang={lang} onChange={(v) => onChange({ formTitle: v })} />
          <p className="text-xs text-zinc-500">Alamat, telepon, email, maps diambil dari Pengaturan.</p>
        </div>
      );
    case "gallery":
      return (
        <div className="space-y-3">
          {(p.images || []).map((url, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="flex-1">
                <FileUpload label={`Gambar ${i + 1}`} value={url} onChange={(v) => onChange({ images: p.images.map((x, k) => (k === i ? v : x)) })} folder="pages" aspect="square" />
              </div>
              <button type="button" onClick={() => onChange({ images: p.images.filter((_, k) => k !== i) })} className="p-2 text-red-600 mt-6"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => onChange({ images: [...(p.images || []), ""] })} className="text-xs text-maroon-800 hover:underline font-semibold">+ Tambah gambar</button>
        </div>
      );
    case "spacer":
      return <F label="Tinggi (px)" value={p.height || 60} onChange={(v) => onChange({ height: parseInt(v, 10) || 60 })} />;
    default:
      return <p className="text-sm text-zinc-500">Tidak ada form untuk tipe ini.</p>;
  }
};

export default PageEditor;
